import { useEffect, useState } from 'react';
import { Trophy, Zap } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 60_000;

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtX = (n) => `${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x`;

const initials = (name) => {
    const parts = (name || '').replace(/\./g, '').split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

/**
 * BigWinLeaderboard
 * -----------------
 * Lobby widget. Two columns:
 *   - Top Multipliers (this week)
 *   - Top Dollar Wins (this week)
 * Renders nothing if no big wins have happened yet.
 */
const BigWinLeaderboard = () => {
    const [mults, setMults] = useState([]);
    const [amounts, setAmounts] = useState([]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/wins/big-leaderboard?limit=5&days=7`);
                if (cancelled) return;
                setMults(data?.top_multipliers || []);
                setAmounts(data?.top_amounts || []);
            } catch { /* ignore */ }
        };
        load();
        const id = setInterval(load, POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    if (mults.length === 0 && amounts.length === 0) return null;

    return (
        <div
            className="rounded-2xl overflow-hidden border border-[#FFD700]/25"
            style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.05) 0%, rgba(255,0,127,0.04) 50%, rgba(57,255,20,0.05) 100%)' }}
            data-testid="big-win-leaderboard"
        >
            <div className="px-4 py-3 border-b border-[#FFD700]/15 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#FFD700]" />
                <h3 className="text-sm font-black text-white tracking-wider uppercase">Big Wins This Week</h3>
                <span className="ml-auto text-[10px] text-[#A0AAB2] font-bold">LAST 7 DAYS</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
                {/* Top Multipliers */}
                <div className="p-4">
                    <div className="flex items-center gap-1.5 mb-3">
                        <Zap className="w-3.5 h-3.5 text-[#39FF14]" />
                        <span className="text-[10px] font-black text-[#39FF14] tracking-[0.15em]">TOP MULTIPLIERS</span>
                    </div>
                    {mults.length === 0 ? (
                        <p className="text-xs text-[#A0AAB2]">No big multipliers yet — be the first!</p>
                    ) : (
                        <ol className="space-y-2">
                            {mults.map((e, i) => (
                                <li
                                    key={`m-${e.id}`}
                                    className="flex items-center gap-2 text-xs"
                                    data-testid={`bwl-mult-${i}`}
                                >
                                    <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-black ${i === 0 ? 'bg-[#FFD700] text-black' : i === 1 ? 'bg-[#C0C0C0]/80 text-black' : i === 2 ? 'bg-[#CD7F32]/80 text-black' : 'bg-white/10 text-[#A0AAB2]'}`}>
                                        {i + 1}
                                    </span>
                                    <span className="w-6 h-6 shrink-0 rounded-full bg-gradient-to-br from-[#FFD700]/40 to-[#FF007F]/40 flex items-center justify-center text-[9px] font-black text-white">
                                        {initials(e.player)}
                                    </span>
                                    <span className="text-white font-semibold truncate flex-1">{e.player}</span>
                                    <span className="text-[#39FF14] font-black tabular-nums">{fmtX(e.multiplier)}</span>
                                    <span className="text-[10px] text-[#A0AAB2] tabular-nums">{fmt$(e.win)}</span>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>

                {/* Top Dollar Wins */}
                <div className="p-4">
                    <div className="flex items-center gap-1.5 mb-3">
                        <Trophy className="w-3.5 h-3.5 text-[#FFD700]" />
                        <span className="text-[10px] font-black text-[#FFD700] tracking-[0.15em]">TOP DOLLAR WINS</span>
                    </div>
                    {amounts.length === 0 ? (
                        <p className="text-xs text-[#A0AAB2]">No big wins yet — your spin could be next.</p>
                    ) : (
                        <ol className="space-y-2">
                            {amounts.map((e, i) => (
                                <li
                                    key={`a-${e.id}`}
                                    className="flex items-center gap-2 text-xs"
                                    data-testid={`bwl-amt-${i}`}
                                >
                                    <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-black ${i === 0 ? 'bg-[#FFD700] text-black' : i === 1 ? 'bg-[#C0C0C0]/80 text-black' : i === 2 ? 'bg-[#CD7F32]/80 text-black' : 'bg-white/10 text-[#A0AAB2]'}`}>
                                        {i + 1}
                                    </span>
                                    <span className="w-6 h-6 shrink-0 rounded-full bg-gradient-to-br from-[#39FF14]/40 to-[#FFD700]/40 flex items-center justify-center text-[9px] font-black text-white">
                                        {initials(e.player)}
                                    </span>
                                    <span className="text-white font-semibold truncate flex-1">{e.player}</span>
                                    <span className="text-[#FFD700] font-black tabular-nums">{fmt$(e.win)}</span>
                                    <span className="text-[10px] text-[#39FF14] tabular-nums">{fmtX(e.multiplier)}</span>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BigWinLeaderboard;
